import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import {
  getReadingStats,
  getMyEnrollments,
  getWebinars
} from '../../services/api';
import {
  BookOpen,
  Clock,
  Video,
  TrendingUp,
  ArrowRight,
  MessageCircle
} from 'lucide-react';
import './StudentDashboard.css';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [webinarsLoading, setWebinarsLoading] = useState(true);
  const [isUpcoming, setIsUpcoming] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setWebinarsLoading(true);

      // First try to fetch upcoming webinars
      const [statsRes, enrollmentsRes, webinarsRes] = await Promise.all([
        getReadingStats(),
        getMyEnrollments({ courseType: 'ebook' }),
        getWebinars({ limit: 3, status: 'upcoming' })
      ]);

      setStats(statsRes.data.stats || {});
      setEnrollments(enrollmentsRes.data.enrollments || []);

      let webinarsData = webinarsRes.data?.data?.webinars || [];

      // If no upcoming webinars, fetch latest ones (fallback)
      if (webinarsData.length === 0) {
        setIsUpcoming(false);
        try {
          const fallbackRes = await getWebinars({ limit: 3, sort: '-scheduledAt' });
          webinarsData = fallbackRes.data?.data?.webinars || [];
        } catch (fallbackErr) {
          console.error('Error fetching fallback webinars:', fallbackErr);
        }
      } else {
        setIsUpcoming(true);
      }

      setWebinars(webinarsData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
      setWebinarsLoading(false);
    }
  };

  const calculateAverageProgress = () => {
    if (!enrollments || enrollments.length === 0) return 0;
    const total = enrollments.reduce((sum, enrollment) => sum + (enrollment.progress || 0), 0);
    return Math.round(total / enrollments.length);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      {/* Welcome Header */}
      <div className="dashboard-header">
        <h1>Welcome back, {user?.firstName || 'Student'}!</h1>
        <p className="subtitle">Track your learning progress and discover new opportunities.</p>
      </div>

      <div className="dashboard-container">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon turquoise">
              <BookOpen size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Courses</p>
              <h3 className="stat-value">{enrollments.length || 0}</h3>
              <p className="stat-description">Enrolled courses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Completed</p>
              <h3 className="stat-value">{stats?.completedEbooks || 0}</h3>
              <p className="stat-description">Finished courses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Progress</p>
              <h3 className="stat-value">{calculateAverageProgress()}%</h3>
              <p className="stat-description">Average completion</p>
            </div>
          </div>

          <Link to="/webinars" className="stat-card" style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <div className="stat-icon purple">
              <Video size={24} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Webinars</p>
              <h3 className="stat-value">{webinars.length}</h3>
              <p className="stat-description">{isUpcoming ? 'Upcoming sessions' : 'Recent sessions'}</p>
            </div>
          </Link>
        </div>

        {/* Continue Learning Section */}
        <div className="continue-learning-section">
          <div className="section-header">
            <h2>Continue Learning</h2>
            <Link to="/my-courses" className="view-all-link">
              View All Courses <ArrowRight size={16} />
            </Link>
          </div>

          <div className="section-content">
            {enrollments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <BookOpen size={64} strokeWidth={1.5} />
                </div>
                <h3>No courses yet</h3>
                <p>Start your learning journey by enrolling in a course.</p>
                <Link to="/courses" className="btn-primary">
                  Browse Courses
                </Link>
              </div>
            ) : (
              <div className="courses-grid">
                {enrollments.slice(0, 3).map((enrollment) => (
                  <Link
                    to={`/courses/${enrollment.course._id}`}
                    key={enrollment._id}
                    className="course-card"
                  >
                    <div className="course-thumbnail">
                      {enrollment.course?.thumbnail ? (
                        <img src={enrollment.course.thumbnail} alt={enrollment.course.title} />
                      ) : (
                        <div className="placeholder-thumbnail">
                          <BookOpen size={48} />
                        </div>
                      )}
                    </div>
                    <div className="course-content">
                      <h3>{enrollment.course?.title || 'Untitled Course'}</h3>
                      <p className="course-category">
                        {enrollment.course?.ebookSubcategory || enrollment.course?.category || 'General'}
                      </p>

                      <div className="progress-section">
                        <div className="progress-info">
                          <span className="progress-text">Progress</span>
                          <span className="progress-percentage">{enrollment.progress || 0}%</span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${enrollment.progress || 0}%` }}
                          ></div>
                        </div>
                      </div>

                      <button className="continue-btn">
                        Continue Learning <ArrowRight size={16} />
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Webinars Section */}
        <div className="webinars-section">
          <div className="section-header">
            <h2>{isUpcoming ? 'Upcoming Webinars' : 'Latest Webinars'}</h2>
            <Link to="/webinars" className="view-all-link">
              View All <ArrowRight size={16} />
            </Link>
          </div>

          <div className="section-content">
            {webinarsLoading ? (
              <div className="loading-state">
                <div className="spinner-small"></div>
                <p>Loading upcoming webinars...</p>
              </div>
            ) : webinars.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <Video size={64} strokeWidth={1.5} />
                </div>
                <p>No upcoming webinars scheduled.</p>
              </div>
            ) : (
              <div className="courses-grid">
                {webinars.map((webinar) => (
                  <Link
                    to={`/webinars/${webinar.id}`}
                    key={webinar.id}
                    className="course-card"
                    style={{ textDecoration: 'none' }}
                  >
                    <div className="course-thumbnail">
                      {webinar.thumbnail_url ? (
                        <img src={webinar.thumbnail_url} alt={webinar.title} />
                      ) : (
                        <div className="placeholder-thumbnail" style={{ backgroundColor: '#6f42c1' }}>
                          <Video size={48} color="white" />
                        </div>
                      )}
                      {webinar.is_live && (
                        <span className="live-badge">LIVE</span>
                      )}
                    </div>
                    <div className="course-content">
                      <h3>{webinar.title}</h3>
                      <p className="course-category">
                        {new Date(webinar.scheduled_at).toLocaleDateString()} • {new Date(webinar.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>

                      <div className="instructor-info" style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div className="instructor-avatar-small" style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>
                          {webinar.instructor_name?.charAt(0) || 'I'}
                        </div>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>{webinar.instructor_name}</span>
                      </div>

                      <button className="continue-btn" style={{ marginTop: '15px' }}>
                        View Details <ArrowRight size={16} />
                      </button>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
